/**
 * Token-compliant sparkline. Unlike the legacy shared `Sparkline`, this uses
 * `currentColor`/theme tokens (no hardcoded white-on-glass), so it renders
 * correctly on light and dark themes.
 */
import React from 'react';

export interface KpiSparklineProps {
  values: number[];
  width?: number;
  height?: number;
  /** Any CSS color, typically a status token expression. Defaults to accent. */
  color?: string;
  /** Fill the area under the line with a faint gradient. */
  filled?: boolean;
  ariaLabel?: string;
}

export const KpiSparkline: React.FC<KpiSparklineProps> = ({
  values,
  width = 168,
  height = 40,
  color = 'var(--main-accent, #6C4CFF)',
  filled = true,
  ariaLabel,
}) => {
  const gradientId = React.useId();

  if (!values || values.length < 2) {
    return (
      <div
        style={{
          width: '100%',
          height,
          display: 'flex',
          alignItems: 'center',
          fontSize: 11,
          color: 'var(--main-text-secondary)',
          opacity: 0.7,
        }}
        aria-hidden="true"
      >
        {values && values.length === 1 ? '' : '\u2014'}
      </div>
    );
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const pad = 3;
  const w = width;
  const chartW = w - pad * 2;
  const chartH = height - pad * 2;
  const stepX = chartW / Math.max(values.length - 1, 1);

  const pts = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + chartH - ((v - min) / range) * chartH;
    return { x, y };
  });
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `${line} L${pts[pts.length - 1].x.toFixed(1)},${height - pad} L${pad},${height - pad} Z`;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block', color }}
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {filled && <path d={area} fill={`url(#${gradientId})`} stroke="none" />}
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

export default KpiSparkline;
